<template>
  <div>
    <v-card min-height="600">
      <v-card-text>
        <v-overlay v-if="isLoading" opacity="1" color="white" absolute>
          <v-progress-circular indeterminate color="green" size="50"></v-progress-circular>
        </v-overlay>
        <template v-else>
          <template v-if="meterData && meterData.length">
            <div class="flex items-center justify-between my-3">
              <h3 class="black--text">{{ $t('meter.digital_electricity_meter_list') }}</h3>
            </div>
            <v-data-table
              :headers="headers"
              :items="meterData"
              :loading="isLoading"
              :items-per-page="50"
              class="border-2"
              :no-data-text="$t('noinfo')"
              :no-results-text="$t('no_matching_records_found')">
              <template v-slot:item.meterStatus.status_on_off="{ item }">
                <template v-if="item.meter.digitalMeterData.meter_type != 'data'">
                  <v-switch v-model="item.meterStatus.status_on_off" inset class="mt-0" color="primary" hide-details
                    :disabled="item.isOffline || !isPermission('enableDigitalMeterOnOff')"
                    @change="confirmChange($event, item)"></v-switch>
                </template>
              </template>
              <template v-slot:item.meter.label="{ item }">
                <div class="d-flex align-center">
                  <div>{{ item.meter.isMain && !item.meter.name ? $t('meter.main_meter') : item.meter.name || $t('meter.sub_meter') }}
                  </div>
                  <div v-if="item.meter.digitalMeterData.meter_type == 'prepaid'" class="ms-2">
                    <v-chip class="chip-status info">
                      <div class="chip-text-sm">{{ $t('prepaid') }}</div>
                    </v-chip>
                  </div>
                </div>
              </template>
              <template v-slot:item.unit="{ item }">
                <div class="d-flex align-center">
                  <div> </div>
                  <div v-if="item.meter.digitalMeterData.meter_type == 'prepaid'">
                    <v-tooltip top color="bgTooltip" v-if="item.beforeMeterData && item.beforeMeterData.unit">
                      <template v-slot:activator="{ on, attrs }">
                        <v-chip v-bind="attrs" v-on="on"
                          :class="['chip-status', { 'info': item.beforeMeterData.unit > 0 }, { 'error': !item.beforeMeterData.unit || item.beforeMeterData.unit <= 0 }]">
                          <template>
                            {{ $helper.toCurrency(item.beforeMeterData.unit || 0) }} {{ $t('meter.unit_kwh') }}
                          </template>
                        </v-chip>
                      </template>
                      <div class="box-tooltip max-w-100">
                        <div class="text-center">{{ $t('last_send_data') }} {{ $helper.dateTimeTH(item.beforeMeterData.datetime) }}
                        </div>
                      </div>
                    </v-tooltip>
                    <v-tooltip top color="bgTooltip" v-else>
                      <template v-slot:activator="{ on, attrs }">
                        <v-chip v-bind="attrs" v-on="on"
                          :class="['chip-status', { 'info': item.unit && item.unit > 0 }, { 'error': !item.unit || item.unit <= 0 }]">
                          <template>
                            {{ $helper.toCurrency(item.unit || 0) }} {{ $t('meter.unit_kwh') }}
                          </template>
                        </v-chip>
                      </template>
                      <div class="box-tooltip max-w-100">
                        <div class="text-center">{{ $t('last_send_data') }} {{ $helper.dateTimeTH(item.datetime) }}</div>
                      </div>
                    </v-tooltip>

                  </div>
                  <div v-else-if="item.meter.digitalMeterData.meter_type == 'relay'">
                    <v-tooltip top color="bgTooltip">
                      <template v-slot:activator="{ on, attrs }">
                        <v-chip v-bind="attrs" v-on="on"
                          class="['chip-status', { 'info': item.unit && item.unit > 0 }, { 'error': !item.unit || item.unit <= 0 }]">
                          {{ $helper.toCurrency(item.unit || 0) }} {{ $t('meter.unit_kwh') }}
                        </v-chip>
                      </template>
                      <div class="box-tooltip max-w-100">
                        <div class="text-center">{{ $t('last_send_data') }} {{ $helper.dateTimeTH(item.datetime) }}</div>
                      </div>
                    </v-tooltip>
                  </div>
                  <div v-else-if="item.meter.digitalMeterData.meter_type == 'data'">
                    <v-tooltip top color="bgTooltip">
                      <template v-slot:activator="{ on, attrs }">
                        <v-chip v-bind="attrs" v-on="on"
                          :class="['chip-status', { 'info': item.unit && item.unit > 0 }, { 'error': !item.unit || item.unit <= 0 }]">
                          {{ $helper.toCurrency(item.unit || 0) }} {{ $t('meter.unit_kwh') }}
                        </v-chip>
                      </template>
                      <div class="box-tooltip max-w-100">
                        <div class="text-center">{{ $t('last_send_data') }} {{ $helper.dateTimeTH(item.datetime) }}</div>
                      </div>
                    </v-tooltip>
                  </div>
                </div>
              </template>

              <template v-slot:item.isOffline="{ item }">
                <v-chip class="chip-status mr-2 my-1" :class="[!item.isOffline ? 'success' : 'darkgray']">
                  <v-icon left x-small> mdi-circle </v-icon>
                  {{ !item.isOffline ? $t('online') : $t('offline') }}
                </v-chip>
              </template>

              <template v-slot:item.actions="{ item }">
                <template v-if="item.meter.digitalMeterData.meter_type == 'prepaid'">
                  <div class="flex justify-end">
                    <v-btn class="btn-sell-unit me-2" elevation="0" small
                      :disabled="item.isOffline || !isPermission('enableEditInvoiceAndReceipt') || !checkAreaAndBooking"
                      @click="openDialogMeterSellUnit(item);">
                      <img v-if="!isPermission('enableEditInvoiceAndReceipt') || !checkAreaAndBooking"
                        :src="require(`@/assets/images/meter-sell-unit-grey.svg`)" class="me-2" />
                      <img v-else-if="!item.isOffline" :src="require(`@/assets/images/meter-sell-unit.svg`)"
                        class="me-2" />
                      <img v-else :src="require(`@/assets/images/meter-sell-unit-grey.svg`)" class="me-2" />
                      <span>{{ $t('meter.sell_unit') }}</span>
                    </v-btn>
                    <v-badge v-if="item.countUnPaid > 0" color="error" :content="item.countUnPaid" overlap
                      offset-x="20">
                      <v-btn class="btn-order me-2" elevation="0" small @click="openDialogOrder(item)">
                        <img :src="require(`@/assets/images/meter-order.svg`)" class="me-2" />
                        <span>{{ $t('payment.order') }}</span>
                      </v-btn>
                    </v-badge>
                    <v-btn v-else class="btn-order me-2" elevation="0" small @click="openDialogOrder(item)">
                      <img :src="require(`@/assets/images/meter-order.svg`)" class="me-2" />
                      <span>{{ $t('payment.order') }}</span>
                    </v-btn>
                    <v-btn class="btn-reset-meter" elevation="0" small
                      :disabled="item.isOffline || !isPermission('enableDigitalMeterReset')"
                      @click="openDialogMeterReset(item)">
                      <img v-if="!isPermission('enableDigitalMeterReset')"
                        :src="require(`@/assets/images/meter-reset-grey.svg`)" class="me-2" />
                      <img v-else-if="!item.isOffline" :src="require(`@/assets/images/meter-reset.svg`)" class="me-2" />
                      <img v-else :src="require(`@/assets/images/meter-reset-grey.svg`)" class="me-2" />
                      <span>{{ $t('meter.reset_meter') }}</span>
                    </v-btn>
                  </div>
                </template>
              </template>
            </v-data-table>
          </template>
          <template v-else>
            <img src="https://images.myket.in.th/banner-prepaid.png" alt="banner-prepaid"
              class="mx-auto d-block mt-4" />
            <div class="text-center mt-2">
              <span class="grey--text">{{ $t('market.ask_for_more_information') }}</span>
              <a href="https://line.me/R/ti/p/@430uwdcb" target="_blank" class="font-weight-bold ml-2">Line: @myketpro</a>
            </div>
          </template>
        </template>
      </v-card-text>
    </v-card>

    <v-dialog v-model="dialogMeterOnOff" persistent max-width="400px">
      <v-card>
        <v-card-title class="justify-center">{{ tempStatus ? $t('turn_on_electricity') : $t('turn_off_electricity') }}</v-card-title>
        <v-card-text>
          <template v-if="switchMeterType == 'prepaid'">
            <div class="black--text text-center">
              <div class="mb-2">{{ $t('validate.you_want_to_turn_on_or_off_electricity', { action: tempStatus ? $t('turn_on') : $t('turn_off') }) }}<span
                  v-if="tempStatus">{{ $t('validate.yes_or_no') }}</span></div>
              <div class="mb-2" v-if="!tempStatus">{{ $t('validate.cancel_auto_turn_off_electricity') }}</div>
            </div>
          </template>
          <template v-if="switchMeterType == 'relay'">
            <div class="black--text text-center">
              <div class="mb-2">{{ $t('validate.you_want_to_turn_on_or_off_electricity_question', { action: tempStatus ? $t('turn_on') : $t('turn_off') }) }}</div>
            </div>
          </template>

          <template v-if="!tempStatus && switchMeterType == 'prepaid'">
            <v-alert border="left" color="warning" dense text type="info" class="alert-on-off mb-2">
              {{ $t('meter.if_the_unit_is_added_the_electricity_will_be_turned_on_automatically') }}
            </v-alert>
          </template>

          <div class="mb-1 black--text">{{ $t('note') }} :</div>
          <v-text-field :label="$t('note')" single-line outlined hide-details v-model="remarkOnOff"
            :disabled="isSaving"></v-text-field>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <div class="flex justify-between">
            <v-btn color="grey" width="49%" outlined @click="cancelChange" :disabled="isSaving">{{ $t('cancel') }}</v-btn>
            <v-btn color="success" width="49%" @click="confirmSwitch" elevation="0" :disabled="isSaving"
              :loading="isSaving">{{ $t('confirm') }}</v-btn>
          </div>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogMeterReset" persistent max-width="400px">
      <v-card>
        <v-card-title class="justify-center">{{ $t('meter.reset_meter') }}</v-card-title>
        <v-card-text>
          <div class="black--text text-center">
            <div class="mb-2">{{ $t('validate.please_input_the_unit_to_reset') }}</div>
          </div>
          <v-currency-field label="0" single-line outlined step="0.1" min="0" dense hide-details
            v-model="formResetMeter.amount" :disabled="isSaving" />
          <div class="mb-1 black--text mt-3">{{ $t('note') }} :</div>
          <v-text-field :label="$t('note')" single-line outlined hide-details v-model="formResetMeter.note"
            :disabled="isSaving"></v-text-field>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <div class="flex justify-between">
            <v-btn color="grey" width="49%" outlined @click="cancelResetMeter" :disabled="isSaving">{{ $t('cancel') }}</v-btn>
            <v-btn color="warning" width="49%" @click="confirmResetMeter" elevation="0" :disabled="isSaving"
              :loading="isSaving">{{ $t('confirm') }}</v-btn>
          </div>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <MeterDigitalOrderListDialog v-model="dialogMeterOrder" :projectId="projectId" :marketId="marketId" :areaId="areaId"
      :meterId="selectedMeter && selectedMeter.meter.objectId ? selectedMeter.meter.objectId : null"
      @onSaveSuccess="initData()" />
    <MeterDigitalSellUnitDialog v-model="dialogMeterSellUnit" :projectId="projectId" :marketId="marketId" :area="area"
      :contract="contract" :booking="booking"
      :meterId="selectedMeter && selectedMeter.meter.objectId ? selectedMeter.meter.objectId : null"
      @onSaveSuccess="initData()" @onClose="cancelDialogMeterSell" />
  </div>
</template>

<script>
import MeterDigitalOrderListDialog from "./MeterDigitalOrderListDialog.vue";
import MeterDigitalSellUnitDialog from "./MeterDigitalSellUnitDialog.vue";
import { mapGetters, mapMutations, mapActions } from "vuex";
export default {
  components: {
    MeterDigitalOrderListDialog,
    MeterDigitalSellUnitDialog,
  },
  props: {
    projectId: String,
    marketId: String,
    area: Object,
    contract: Object,
    booking: Object,
  },
  computed: {
    ...mapGetters({
      getMetersElec: 'meter/getMetersElec',
    }),
    admin() {
      return this.$store.getters["admin/getByMarketId"](this.marketId);
    },
    employee() {
      return this.$store.getters["employee/getByMarketId"](this.marketId);
    },
    checkAreaAndBooking() {
      if (this.booking || (this.contract && this.contract.isActive)) {
        return true;
      } else {
        return false;
      }
    }
  },
  data() {
    return {
      isLoading: false,
      isSaving: false,
      headers: [],
      meterData: [],
      formResetMeter: {
        amount: 0,
        note: null,
      },
      remarkOnOff: null,
      dialogMeterReset: false,
      dialogMeterOnOff: false,
      dialogMeterOrder: false,
      dialogMeterSellUnit: false,
      tempItem: null,
      tempStatus: null,
      selectedMeter: null,
      hashMeterData: {},
      areaId: null,
      switchMeterType: null,
    }
  },
  mounted() {
    this.initData();
  },
  created() {
    this.headers = [
        {
          text: this.$t('meter.turn_on_or_off_electricity'),
          align: "left",
          sortable: false,
          value: "meterStatus.status_on_off",
          width: "100px",
        },
        {
          text: this.$t('meter.meter_name'),
          align: "left",
          sortable: false,
          value: "meter.label",
        },
        {
          text: this.$t('meter.current_unit'),
          align: "left",
          value: "unit",
        },
        {
          align: "left",
          value: "isOffline",
          sortable: false,
          text: this.$t('status')
        },
        {
          text: this.$t('manage'),
          align: "left",
          sortable: false,
          value: "actions",
          width: "400px"
        }
    ];
  },
  methods: {
    ...mapActions({
      getMeterByArea: 'meter/getMeterByArea',
      getLatestMeterData: 'digitalMeter/getLatestMeterData',
      getLatestMeterStatus: 'digitalMeter/getLatestMeterStatus',
      turnOnDigitalMeter: 'digitalMeter/turnOnDigitalMeter',
      turnOffDigitalMeter: 'digitalMeter/turnOffDigitalMeter',
      resetBalanceDigitalMeter: 'digitalMeter/resetBalanceDigitalMeter',
      getOrderUnPaidCoutByMeter: 'order/getOrderUnPaidCoutByMeter',
    }),
    async initData() {
      this.isLoading = true;
      try {
        this.areaId = this.area.objectId;
        await this.getMeterByArea({
          areaId: this.areaId,
          isDigitalMeter: true,
        });

        this.hashMeterData = {};
        const meters = this.getMetersElec.filter(data => data.digitalMeterData);
        // Get latest meter data and status
        const [meterDataResults, meterStatusResults] = await Promise.all([
          this.getLatestMeterDataForMeters(meters),
          this.getLatestMeterStatusForMeters(meters)
        ]);

        await this.processMeterData(meters, meterDataResults, meterStatusResults);

        this.meterData = this.hashMeterData[this.areaId];
        this.isLoading = false;

      } catch (err) {
        this.isLoading = false;
      }
    },

    async getLatestMeterDataForMeters(meters) {
      const promises = meters.map(data => {
        const { partner, type, cust_id, meter_id } = data.digitalMeterData;
        return this.getLatestMeterData({
          partner,
          type,
          cust_id,
          meterAddress: meter_id,
          datetime: new Date()
        });
      });

      const results = await Promise.all(promises);
      return results.reduce((acc, result) => ({ ...acc, ...result }), {});
    },

    async getLatestMeterStatusForMeters(meters) {
      const promises = meters.map(data => {
        const { partner, type, cust_id, meter_id } = data.digitalMeterData;
        return this.getLatestMeterStatus({
          partner,
          type,
          cust_id,
          meterAddress: meter_id
        });
      });

      const results = await Promise.all(promises);
      return results.reduce((acc, result) => ({ ...acc, ...result }), {});
    },

    async processMeterData(meters, meterDataResults, meterStatusResults) {
      for (const meter of meters) {
        const { cust_id, meter_id, type } = meter.digitalMeterData;
        const key = `${cust_id}_${meter_id}`;

        let meterData = meterDataResults[key];
        if (!meterData) {
          meterData = {
            isOffline: true,
            unit: 0,
            datetime: meter.updatedAt,
            type: type,
          };
        }
        if (!this.hashMeterData[meter.area.objectId]) {
          this.hashMeterData[meter.area.objectId] = [];
        }
        const meterInfo = {
          ...meterData,
          meter,
          countUnPaid: type === "prepaid" ? await this.getOrderUnPaidCoutByMeter(meter.objectId) : null,
          beforeMeterData: meterData.isOffline ? await this.getOfflineMeterData(meter) : null
        };

        const meterStatus = meterStatusResults[key];
        if (meterStatus) {
          meterInfo.meterStatus = {
            ...meterStatus,
            status_on_off: meterStatus.status_on_off === 'on'
          };
        }

        this.hashMeterData[meter.area.objectId].push(meterInfo);
      }
    },

    async getOfflineMeterData(meter) {
      try {
        const { partner, type, cust_id } = meter.digitalMeterData;
        const results = await this.getLatestMeterData({
          partner,
          type,
          cust_id,
          meterAddress: null,
          datetime: new Date()
        });
        return results[`${cust_id}_${meter.digitalMeterData.meter_id}`] || [];
      } catch (error) {
        this.$toast.show({
          content: error.message,
          color: "error"
        });
        return [];
      }
    },
    confirmChange(newValue, item) {
      this.remarkOnOff = null;
      this.tempItem = item;
      this.tempStatus = newValue;
      this.switchMeterType = item.meter.digitalMeterData.meter_type;
      this.$nextTick(() => {
        this.dialogMeterOnOff = true;
        item.meterStatus.status_on_off = !newValue;
      });
    },
    async confirmSwitch() {
      this.isSaving = true;
      if (this.tempItem) {
        this.tempItem.meterStatus.status_on_off = this.tempStatus;
        try {
          if (this.tempItem.meterStatus.status_on_off) {
            let result = await this.turnOnDigitalMeter({
              meterId: this.tempItem.meter.objectId,
              note: this.remarkOnOff
            })
            if (result && result.success) {
              this.$toast.show({
                content: this.$t('meter.turn_on_electricity_successfully'),
                color: "success"
              });
            } else {
              this.$toast.show({
                content: this.$t('meter.turn_on_electricity_failed'),
                color: "error"
              });
            }
            await this.initData();
          } else {
            let result = await this.turnOffDigitalMeter({
              meterId: this.tempItem.meter.objectId,
              note: this.remarkOnOff
            })
            if (result && result.success) {
              this.$toast.show({
                content: this.$t('meter.turn_off_electricity_successfully'),
                color: "success"
              });
            } else {
              this.$toast.show({
                content: this.$t('meter.turn_off_electricity_failed'),
                color: "error"
              });
            }
            await this.initData();
          }
          this.isSaving = false;
          this.dialogMeterOnOff = false;
          this.$emit('reloadContract')
        } catch (error) {
          this.isSaving = false;
          this.dialogMeterOnOff = false;
          if (error?.response?.data?.error) {
            this.$toast.show({
              content: error.response.data.error,
              color: "error"
            });
          } else {
            this.$toast.show({
              content: error.message,
              color: "error"
            });
          }
        }
      }
    },
    cancelChange() {
      this.tempItem = null;
      this.tempStatus = null;
      this.dialogMeterOnOff = false;
    },
    openDialogMeterReset(data) {
      this.dialogMeterReset = true;
      this.selectedMeter = data;
    },
    async confirmResetMeter() {
      this.isSaving = true;
      try {
        let result = await this.resetBalanceDigitalMeter({
          meterId: this.selectedMeter.meter.objectId,
          amount: this.formResetMeter.amount,
          note: this.formResetMeter.note,
        })
        if (result && result.success) {
          this.$toast.show({
            content: this.$t('meter.reset_meter_successfully'),
            color: "success"
          });
          await this.initData();
          this.$emit('reloadContract')
        }
        this.isSaving = false;
        this.dialogMeterReset = false;
      } catch (error) {
        this.isSaving = false;
        this.dialogMeterReset = false;
        if (error?.response?.data?.error) {
          this.$toast.show({
            content: error.response.data.error,
            color: "error"
          });
        } else {
          this.$toast.show({
            content: error.message,
            color: "error"
          });
        }
      }
    },
    cancelResetMeter() {
      this.dialogMeterReset = false;
      this.selectedMeter = null;
      this.formResetMeter.amount = 0;
      this.formResetMeter.note = null;
    },
    openDialogOrder(data) {
      this.selectedMeter = data;
      this.dialogMeterOrder = true;
    },
    openDialogMeterSellUnit(data) {
      this.selectedMeter = data;
      this.dialogMeterSellUnit = true;
    },
    cancelDialogMeterSell() {
      this.selectedMeter = null;
    },
    isPermission(rule) {
      if (this.admin || (this.employee && this.employee[rule])) {
        return true
      }
      return false
    }
  },
  watch: {

  },
};
</script>

<style lang="scss" scoped>
.btn-sell-unit {
  background-color: var(--v-primary-base) !important;
  color: var(--v-light-base);

  &:disabled {
    svg path {
      fill: #A9A9A9;
    }
  }
}

.btn-order {
  background-color: rgba(71, 189, 130, 0.3) !important;
  color: var(--v-primary-base);

  &:disabled {
    img {
      color: #A9A9A9;
    }

    ;

    svg path {
      fill: #A9A9A9;
    }
  }
}

.btn-reset-meter {
  background-color: rgba(251, 174, 67, 0.3) !important;
  color: #FBAE43;

  &:disabled {
    img {
      color: #A9A9A9;
    }

    ;

    svg path {
      fill: #A9A9A9;
    }

    ;
  }
}

.chip-text-sm {
  font-size: 12px;
  font-weight: 500
}
</style>

<style lang="scss">
.alert-on-off {
  .v-alert__icon {
    margin-right: 5px !important;
  }

  .v-alert__border {
    opacity: 1;
    border-width: 4px;
  }
}
</style>
